import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { TIDS } from "@/constants/testIds";
import { Plus, Route as RouteIcon, ExternalLink, Copy, Trash2, ChevronRight } from "lucide-react";

export default function Funnels() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/funnels");
    setRows(data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/funnels", { name: name.trim(), steps: [] });
      setRows((r) => [data, ...r]);
      setName(""); setShowNew(false);
      toast.success("Funnel created");
      // Navigate to builder
      window.location.href = `/app/funnels/${data.id}`;
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this funnel?")) return;
    try {
      await api.delete(`/funnels/${id}`);
      setRows((r) => r.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const copyLink = (slug) => {
    navigator.clipboard.writeText(`${window.location.origin}/f/${slug}`);
    toast.success("Funnel link copied");
  };

  return (
    <div className="space-y-6 vp-fade-up">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="vp-label">Funnels</div>
          <h1 className="font-display text-3xl font-bold mt-1">Multi-page funnels</h1>
          <p className="text-[#4A4D57] mt-2 text-sm max-w-xl">Chain landing pages into a converting flow — landing → sales → checkout → thank-you. Track drop-off at each step.</p>
        </div>
        <button data-testid={TIDS.funnelNewBtn} onClick={() => setShowNew(true)} className="vp-btn-primary inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> New funnel
        </button>
      </div>

      {loading ? (
        <div className="text-[#4A4D57]">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="vp-card p-10 text-center">
          <div className="w-12 h-12 rounded-2xl bg-[#0B5CFF]/10 flex items-center justify-center mx-auto mb-4">
            <RouteIcon className="w-6 h-6 text-[#0B5CFF]" strokeWidth={1.5} />
          </div>
          <div className="font-display font-bold text-xl">No funnels yet</div>
          <p className="text-[#4A4D57] mt-2 text-sm max-w-md mx-auto">Create your first funnel by chaining 2 or more landing pages together.</p>
          <button onClick={() => setShowNew(true)} className="vp-btn-primary mt-5">Create your first funnel</button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {rows.map((f) => (
            <div key={f.id} data-testid={TIDS.funnelRow} className="vp-card vp-card-hover p-5 flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <Link to={`/app/funnels/${f.id}`} className="font-display font-bold text-lg hover:text-[#0B5CFF] leading-snug">{f.name}</Link>
                <span className={"vp-chip " + (f.status === "active" ? "vp-chip-accent" : "")}>{f.status}</span>
              </div>
              <div className="text-xs text-[#747680] mt-2">Slug · <span className="font-mono">/f/{f.slug}</span> · {f.steps?.length || 0} steps</div>
              {f.steps?.length > 0 && (
                <div className="mt-3 flex items-center flex-wrap gap-1 text-xs">
                  {f.steps.map((s, i) => (
                    <span key={s.step_id} className="inline-flex items-center gap-1">
                      <span className="px-2 py-0.5 rounded-md bg-[#F1F3F7] text-[#0B0D17]">{s.label}</span>
                      {i < f.steps.length - 1 && <ChevronRight className="w-3 h-3 text-[#747680]" />}
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-auto pt-4 flex gap-2 flex-wrap">
                <Link to={`/app/funnels/${f.id}`} className="vp-btn-primary text-xs inline-flex items-center gap-1 py-1.5 px-3">Edit</Link>
                {f.status === "active" && (
                  <button data-testid={TIDS.funnelCopyLinkBtn} onClick={() => copyLink(f.slug)} className="vp-btn-ghost text-xs inline-flex items-center gap-1 py-1.5 px-3">
                    <Copy className="w-3.5 h-3.5" /> Link
                  </button>
                )}
                {f.status === "active" && (
                  <a href={`/f/${f.slug}`} target="_blank" rel="noreferrer" className="vp-btn-ghost text-xs inline-flex items-center gap-1 py-1.5 px-3">
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
                <button onClick={() => remove(f.id)} className="ml-auto p-1.5 text-[#4A4D57] hover:text-[#B00020] rounded-md hover:bg-[#FDE8EA]">
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showNew && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-5" onClick={() => !busy && setShowNew(false)}>
          <div className="vp-glass p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-display font-bold text-xl">New funnel</h2>
            <p className="text-sm text-[#4A4D57] mt-1">Give it a name — you'll add steps in the next screen.</p>
            <input
              data-testid={TIDS.funnelNameInput}
              autoFocus
              className="vp-input mt-4"
              placeholder="e.g. Cohort launch funnel"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && create()}
            />
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setShowNew(false)} disabled={busy} className="vp-btn-ghost text-sm">Cancel</button>
              <button data-testid={TIDS.funnelCreateBtn} onClick={create} disabled={busy || !name.trim()} className="vp-btn-primary text-sm">
                {busy ? "Creating…" : "Create funnel"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
